import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { fetchCollection } from '../lib/cms';
import LogoIconDecoration from '../components/LogoIconDecoration';

export default function Home() {
  const [services, setServices] = useState([]);

  useEffect(() => {
    async function loadServices() {
      const data = await fetchCollection('services');
      setServices(data);
    }
    loadServices();
  }, []);

  const coreBenefits = [
    {
      title: 'ONE POINT OF CONTACT',
      icon: 'fa-user-tie-hair',
      description: 'Your designated representative becomes your go-to source for updates, information, and queries, minimizing the risk of misunderstandings.',
    },
    {
      title: 'LESS TRAVEL TIME',
      icon: 'fa-clock',
      description: 'By establishing high-quality accommodations close to your project site, we save thousands of travel hours for your workforce.',
    },
    {
      title: 'INCREASED SAFETY',
      icon: 'fa-shield-check',
      description: 'Shorter travel distances and certified, fully approved facilities guarantee reliable and quick operations under any conditions.',
    },
  ];

  return (
    <div className="pt-[88px] md:pt-[112px]">
      {/* Hero Section */}
      <section className="relative min-h-[85vh] lg:h-[859px] flex items-center bg-primary text-white overflow-hidden">
        {/* Background Video */}
        <div className="absolute inset-0 z-0">
          <video
            src="https://assets.auxodev.dk/assets/6b13c390-5e11-4ba2-a04f-8e3fde27ab67/site-facility-a-s-site-hotel.mp4"
            poster="https://assets.auxodev.dk/cdn-cgi/image/width=1600,quality=80,format=auto/assets/6b13c390-5e11-4ba2-a04f-8e3fde27ab67/img-0758-scaled.jpg"
            autoPlay
            loop
            muted
            playsInline
            className="h-full w-full object-cover object-[50%_20%]"
            style={{ objectPosition: '50% 20%' }}
          >
            <source src="https://assets.auxodev.dk/assets/6b13c390-5e11-4ba2-a04f-8e3fde27ab67/site-facility-a-s-site-hotel.mp4" type="video/mp4" />
          </video>
          {/* Semi-transparent dark overlay */}
          <div className="absolute inset-0 bg-primary/40 bg-gradient-to-r from-primary/55 via-primary/40 to-primary/25" />
        </div>

        <div className="relative z-10 mx-auto max-w-7xl px-6 md:px-12 py-20 w-full">
          <div className="max-w-5xl space-y-6">
            <h1 
              className="text-2xl sm:text-3xl md:text-5xl lg:text-6xl font-extrabold tracking-tight text-white leading-tight"
              style={{ textShadow: '2px 2px 8px rgba(0, 0, 0, 0.9), 0 0 20px rgba(0, 0, 0, 0.7)' }}
            >
              One stop shop for Accommodation projects, world wide
            </h1>
            <p 
              className="text-lg md:text-xl text-slate-100 leading-relaxed max-w-3xl"
            >
              Site Facility ApS delivers tailored accommodation solutions, camps, hotel management, floatels, and site offices for large-scale projects across Denmark and internationally.
            </p>
            <div className="pt-4 flex flex-wrap gap-4">
              <Link
                to="/services"
                className="py-3.5 px-8 rounded-xl bg-accent text-white font-semibold hover:bg-accent-light transition-all-300 shadow-md hover:shadow-lg cursor-pointer"
              >
                Explore Our Solutions
              </Link>
              <a
                href="#site-facility-video"
                className="inline-flex items-center gap-3 py-3.5 px-7 rounded-xl bg-white/15 hover:bg-white/25 text-white font-semibold transition-all-300 border border-white/20 backdrop-blur-sm cursor-pointer shadow-sm group"
              >
                <span className="flex items-center justify-center w-6 h-6 rounded-full bg-accent text-white text-[10px] group-hover:scale-110 transition-transform">
                  <i className="fa-solid fa-play ml-0.5" />
                </span>
                <span>See Whole Video</span>
              </a>
              <Link
                to="/contact"
                className="py-3.5 px-8 rounded-xl bg-white/10 text-white font-semibold hover:bg-white/20 transition-all-300 border border-white/10 cursor-pointer"
              >
                Contact Us
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Core Benefits Section */}
      <section className="py-20 bg-white border-b border-slate-100 relative overflow-hidden">
        <LogoIconDecoration className="w-[500px] h-[500px] opacity-[0.02] -right-24 -bottom-24 rotate-12" />
        <div className="mx-auto max-w-7xl px-6 md:px-12 relative z-10">
          <div className="text-center max-w-3xl mx-auto mb-16 space-y-4">
            <h2 className="text-xl sm:text-2xl md:text-4xl font-bold tracking-tight">
              Core Benefits of Site Facility
            </h2>
            <p className="text-slate-500 leading-relaxed">
              We streamline accommodations management to introduce specialized expertise, cost savings, and operational efficiency to your project.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {coreBenefits.map((benefit, idx) => (
              <div 
                key={idx} 
                className={`p-8 rounded-2xl border border-slate-100 bg-slate-50/50 flex flex-col items-start space-y-5 ${
                  idx === 2 ? 'md:col-span-2 lg:col-span-1' : ''
                }`}
              >
                <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-primary/5 text-accent">
                  <i className={`fa-regular ${benefit.icon} text-2xl`} />
                </div>
                <h3 className="text-lg font-bold tracking-wider text-primary uppercase">
                  {benefit.title}
                </h3>
                <p className="text-slate-600 text-sm leading-relaxed">
                  {benefit.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Trusted Partners Section */}
      <section className="py-20 bg-slate-50 border-b border-slate-100 overflow-hidden">
        <div className="mx-auto max-w-7xl px-6 md:px-12">
          <div className="text-center max-w-3xl mx-auto mb-14 space-y-3">
            <span className="text-accent text-xs font-bold tracking-widest uppercase">Our Collaborations</span>
            <h2 className="text-lg sm:text-xl md:text-3xl font-bold tracking-tight text-primary">
              Trusted by Industry Leaders
            </h2>
            <p className="text-slate-500 text-sm leading-relaxed">
              We have successfully delivered tailored accommodation solutions for some of the world's most renowned companies.
            </p>
          </div>
        </div>

        {/* Infinite Marquee Slider */}
        <div className="relative w-full overflow-hidden py-4">
          {/* Left and Right Fade Overlays */}
          <div className="absolute left-0 top-0 bottom-0 w-16 md:w-32 bg-gradient-to-r from-slate-50 to-transparent z-10 pointer-events-none" />
          <div className="absolute right-0 top-0 bottom-0 w-16 md:w-32 bg-gradient-to-l from-slate-50 to-transparent z-10 pointer-events-none" />

          {/* Scrolling Track */}
          <div className="flex gap-8 items-center w-max animate-marquee hover:[animation-play-state:paused] cursor-pointer">
            {/* First Set of Logos */}
            {[
              { name: 'Siemens', logo: 'https://assets.auxodev.dk/cdn-cgi/image/width=300,quality=90,format=auto/assets/6b13c390-5e11-4ba2-a04f-8e3fde27ab67/siemens.jpg' },
              { name: 'Vestas', logo: 'https://assets.auxodev.dk/cdn-cgi/image/width=300,quality=90,format=auto/assets/6b13c390-5e11-4ba2-a04f-8e3fde27ab67/vestas.jpg' },
              { name: 'Wobben Windpower', logo: 'https://assets.auxodev.dk/cdn-cgi/image/width=300,quality=90,format=auto/assets/6b13c390-5e11-4ba2-a04f-8e3fde27ab67/wobben.jpg' },
              { name: 'Mammoet', logo: 'https://assets.auxodev.dk/cdn-cgi/image/width=300,quality=90,format=auto/assets/6b13c390-5e11-4ba2-a04f-8e3fde27ab67/mammoet.jpg' },
              { name: 'Enercon', logo: 'https://assets.auxodev.dk/cdn-cgi/image/width=300,quality=90,format=auto/assets/6b13c390-5e11-4ba2-a04f-8e3fde27ab67/enercon.jpg' },
              { name: 'Total Wind', logo: 'https://assets.auxodev.dk/cdn-cgi/image/width=300,quality=90,format=auto/assets/6b13c390-5e11-4ba2-a04f-8e3fde27ab67/total-wind.jpg' },
            ].concat([
              { name: 'Siemens', logo: 'https://assets.auxodev.dk/cdn-cgi/image/width=300,quality=90,format=auto/assets/6b13c390-5e11-4ba2-a04f-8e3fde27ab67/siemens.jpg' },
              { name: 'Vestas', logo: 'https://assets.auxodev.dk/cdn-cgi/image/width=300,quality=90,format=auto/assets/6b13c390-5e11-4ba2-a04f-8e3fde27ab67/vestas.jpg' },
              { name: 'Wobben Windpower', logo: 'https://assets.auxodev.dk/cdn-cgi/image/width=300,quality=90,format=auto/assets/6b13c390-5e11-4ba2-a04f-8e3fde27ab67/wobben.jpg' },
              { name: 'Mammoet', logo: 'https://assets.auxodev.dk/cdn-cgi/image/width=300,quality=90,format=auto/assets/6b13c390-5e11-4ba2-a04f-8e3fde27ab67/mammoet.jpg' },
              { name: 'Enercon', logo: 'https://assets.auxodev.dk/cdn-cgi/image/width=300,quality=90,format=auto/assets/6b13c390-5e11-4ba2-a04f-8e3fde27ab67/enercon.jpg' },
              { name: 'Total Wind', logo: 'https://assets.auxodev.dk/cdn-cgi/image/width=300,quality=90,format=auto/assets/6b13c390-5e11-4ba2-a04f-8e3fde27ab67/total-wind.jpg' },
            ]).concat([
              { name: 'Siemens', logo: 'https://assets.auxodev.dk/cdn-cgi/image/width=300,quality=90,format=auto/assets/6b13c390-5e11-4ba2-a04f-8e3fde27ab67/siemens.jpg' },
              { name: 'Vestas', logo: 'https://assets.auxodev.dk/cdn-cgi/image/width=300,quality=90,format=auto/assets/6b13c390-5e11-4ba2-a04f-8e3fde27ab67/vestas.jpg' },
              { name: 'Wobben Windpower', logo: 'https://assets.auxodev.dk/cdn-cgi/image/width=300,quality=90,format=auto/assets/6b13c390-5e11-4ba2-a04f-8e3fde27ab67/wobben.jpg' },
              { name: 'Mammoet', logo: 'https://assets.auxodev.dk/cdn-cgi/image/width=300,quality=90,format=auto/assets/6b13c390-5e11-4ba2-a04f-8e3fde27ab67/mammoet.jpg' },
              { name: 'Enercon', logo: 'https://assets.auxodev.dk/cdn-cgi/image/width=300,quality=90,format=auto/assets/6b13c390-5e11-4ba2-a04f-8e3fde27ab67/enercon.jpg' },
              { name: 'Total Wind', logo: 'https://assets.auxodev.dk/cdn-cgi/image/width=300,quality=90,format=auto/assets/6b13c390-5e11-4ba2-a04f-8e3fde27ab67/total-wind.jpg' },
            ]).map((partner, idx) => (
              <div 
                key={idx} 
                className="h-24 w-52 md:w-64 flex-shrink-0 flex items-center justify-center p-2 bg-white rounded-2xl border border-slate-100/80 shadow-xs hover:shadow-md hover:border-slate-200/50 transition-all duration-300"
              >
                <img
                  src={partner.logo}
                  alt={`${partner.name} logo`}
                  className="w-full h-full object-contain filter grayscale hover:grayscale-0 transition-all duration-300"
                />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-24 bg-white relative overflow-hidden">
        <LogoIconDecoration className="w-[600px] h-[600px] opacity-[0.015] -right-32 top-1/4 rotate-45 pointer-events-none" />
        <div className="mx-auto max-w-7xl px-6 md:px-12 relative z-10">
          <div className="text-center max-w-3xl mx-auto mb-20 space-y-4">
            <span className="text-accent text-xs font-bold tracking-widest uppercase block">Tailored Solutions</span>
            <h2 className="text-2xl sm:text-3xl md:text-5xl font-extrabold tracking-tight text-primary">
              Our Solutions
            </h2>
            <div className="h-1 w-12 bg-accent mx-auto rounded-full my-4"></div>
            <p className="text-slate-500 text-base md:text-lg leading-relaxed">
              We deliver hotel-grade accommodations directly to your specified locations, ensuring comfort, safety, and efficiency.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-12 gap-8 lg:gap-10">
            {services.map((service, idx) => {
              const total = services.length;
              const remainder3 = total % 3;
              const remainder2 = total % 2;

              let spanClass = 'md:col-span-6 lg:col-span-4';
              if (remainder3 === 1 && idx === total - 1) {
                spanClass = `${remainder2 === 1 ? 'md:col-span-12' : 'md:col-span-6'} lg:col-span-12`;
              } else if (remainder3 === 2 && idx >= total - 2) {
                spanClass = `${remainder2 === 1 && idx === total - 1 ? 'md:col-span-12' : 'md:col-span-6'} lg:col-span-6`;
              } else if (remainder2 === 1 && idx === total - 1) {
                spanClass = 'md:col-span-12 lg:col-span-4';
              }

              return (
                <Link 
                  key={service.id} 
                  to={`/services/${service.slug.en}`}
                  className={`group bg-white rounded-2xl border border-slate-100 shadow-xs hover:shadow-xl hover:border-slate-200/60 hover:-translate-y-2 transition-all duration-500 flex flex-col justify-between overflow-hidden cursor-pointer ${spanClass}`}
                >
                <div>
                  {/* Image Container */}
                  <div className="h-64 overflow-hidden relative">
                    <img
                      src={`https://assets.auxodev.dk/cdn-cgi/image/width=600,quality=85,format=auto/${service.image[0]}`}
                      alt={service.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700 ease-out"
                    />
                    {/* Gradient Overlay for Image */}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                    
                    {/* Premium Icon Badge */}
                    <div className="absolute top-4 right-4 h-12 w-12 rounded-xl bg-white/95 backdrop-blur-md flex items-center justify-center text-accent shadow-md border border-white/20 transform group-hover:scale-110 transition-transform duration-300">
                      <i className={`fa-regular ${service.icon} text-xl`} />
                    </div>
                  </div>

                  {/* Content */}
                  <div className="p-8 space-y-4">
                    <h3 className="text-xl md:text-2xl font-bold text-primary group-hover:text-accent transition-colors duration-300">
                      {service.title}
                    </h3>
                    <p className="text-slate-500 text-sm md:text-base leading-relaxed line-clamp-3">
                      {service.summary}
                    </p>
                  </div>
                </div>

                {/* Footer Link */}
                <div className="px-8 pb-8 pt-0">
                  <span
                    className="inline-flex items-center gap-2.5 text-sm font-bold text-accent group/btn"
                  >
                    <span className="relative after:absolute after:bottom-0 after:left-0 after:h-[2px] after:w-0 group-hover:after:w-full after:bg-accent after:transition-all after:duration-300 pb-0.5">
                      Read More
                    </span>
                    <i className="fa-regular fa-arrow-right text-xs transform group-hover/btn:translate-x-1.5 transition-transform duration-300" />
                  </span>
                </div>
              </Link>
            );
          })}
          </div>
        </div>
      </section>

      {/* The Site Facility Way Section */}
      <section className="py-20 bg-white border-y border-slate-100 relative overflow-hidden">
        <LogoIconDecoration className="w-[450px] h-[450px] opacity-[0.02] -left-20 top-10 -rotate-12" />
        <div className="mx-auto max-w-7xl px-6 md:px-12 space-y-16 relative z-10">
          
          {/* Row 1: Text Content & Image Side-by-Side */}
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            {/* Text Column */}
            <div className="space-y-6 lg:col-span-6">
              <span className="text-accent text-xs font-bold tracking-widest uppercase">The Site Facility Way</span>
              <h2 className="text-xl sm:text-2xl md:text-4xl font-bold tracking-tight">
                Highly Skilled Professionals & Local Subcontractors
              </h2>
              <p className="text-slate-600 leading-relaxed">
                We pride ourselves on assembling a team of highly skilled project managers, site managers, and chefs who operate under carefully tailored contracts. The selection process is meticulous, driven by a keen understanding of our clients’ project requirements and the unique skill sets of each professional.
              </p>
              <p className="text-slate-600 leading-relaxed">
                In our camp setup procedures, we adhere to a standard practice of engaging with local subcontractors sourced through Site Facility. This deliberate approach not only underscores our commitment to social responsibility within the local community but also proves instrumental in achieving cost-efficiency.
              </p>
              <div className="pt-4">
                <Link
                  to="/references"
                  className="inline-flex items-center gap-2 py-3 px-6 rounded-xl bg-primary text-white font-semibold hover:bg-primary-light transition-all duration-300 shadow-sm"
                >
                  <span>View Our References</span>
                  <i className="fa-regular fa-arrow-right text-xs" />
                </Link>
              </div>
            </div>

            {/* Image Column */}
            <div className="lg:col-span-6">
              <div className="relative rounded-2xl overflow-hidden shadow-lg aspect-[16/10] w-full group bg-slate-100">
                <img
                  src="https://assets.auxodev.dk/cdn-cgi/image/width=800,quality=80,format=auto/assets/6b13c390-5e11-4ba2-a04f-8e3fde27ab67/moduler-scaled.jpg"
                  alt="Site Facility Modules and Camp Setup"
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-103"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent pointer-events-none" />
              </div>
            </div>
          </div>

          {/* Row 2: Full-Width Video Player */}
          <div id="site-facility-video" className="relative rounded-2xl overflow-hidden shadow-2xl bg-slate-900 w-full h-[320px] md:h-[480px] lg:h-[600px] flex scroll-mt-24 md:scroll-mt-28">
            <iframe
              src="https://www.youtube.com/embed/JQwmf09gO64?si=U7H3gYbvmcFPR_XB"
              title="YouTube video player"
              className="absolute inset-0 w-full h-full border-0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
              referrerPolicy="strict-origin-when-cross-origin"
              allowFullScreen
            />
          </div>

        </div>
      </section>

      {/* Your Dedicated Point of Contact Section */}
      <section className="py-20 bg-surface relative overflow-hidden">
        <LogoIconDecoration className="w-[400px] h-[400px] opacity-[0.02] right-10 top-1/2 -translate-y-1/2 rotate-45" />
        <div className="mx-auto max-w-7xl px-6 md:px-12 grid grid-cols-1 lg:grid-cols-2 gap-12 items-center relative z-10">
          <div className="order-2 lg:order-1 relative rounded-2xl overflow-hidden shadow-lg aspect-video lg:aspect-square">
            <img
              src="https://assets.auxodev.dk/cdn-cgi/image/width=800,quality=80,format=auto/assets/6b13c390-5e11-4ba2-a04f-8e3fde27ab67/bild-1-1.jpg"
              alt="Dedicated Point of Contact"
              className="w-full h-full object-cover"
            />
          </div>

          <div className="order-1 lg:order-2 space-y-6">
            <span className="text-accent text-xs font-bold tracking-widest uppercase">Streamlined Communication</span>
            <h2 className="text-xl sm:text-2xl md:text-4xl font-bold tracking-tight">
              Your Dedicated Single Point of Contact
            </h2>
            <p className="text-slate-600 leading-relaxed">
              We prioritize efficiency, and our commitment to simplifying your experience is exemplified through your exclusive Single Point of Contact. This personalized service acts as a direct conduit, streamlining communication for a more effective project management process.
            </p>
            <p className="text-slate-600 leading-relaxed">
              By having a designated representative, you benefit from streamlined and clear communication. Your Single Point of Contact becomes your go-to source for updates, information, and queries, minimizing the risk of misunderstandings that can arise in complex, multi-channel structures.
            </p>
            <div className="pt-2">
              <Link
                to="/contact"
                className="inline-flex items-center gap-2 py-3 px-6 rounded-xl bg-accent text-white font-semibold hover:bg-accent-light transition-all-300 shadow-sm"
              >
                <span>Contact Your Liaison</span>
                <i className="fa-regular fa-arrow-right text-xs" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-primary text-white relative overflow-hidden">
        <div className="absolute inset-0 z-0 opacity-10">
          <img
            src="https://assets.auxodev.dk/cdn-cgi/image/width=1600,quality=80,format=auto/assets/6b13c390-5e11-4ba2-a04f-8e3fde27ab67/img-0230.jpg"
            alt="Site Facility Dining"
            className="h-full w-full object-cover"
          />
        </div>
        <div className="relative z-10 mx-auto max-w-5xl px-6 text-center space-y-6">
          <h2 className="text-xl sm:text-2xl md:text-4xl font-bold tracking-tight text-white">
            Ready to Streamline Your Project's Accommodations?
          </h2>
          <p className="text-slate-300 max-w-2xl mx-auto leading-relaxed">
            Get in touch with our team today to discuss your project requirements and let us craft a bespoke, sustainable accommodation solution tailored to your needs.
          </p>
          <div className="pt-4">
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 py-4 px-8 rounded-xl bg-accent text-white font-semibold hover:bg-accent-light transition-all-300 shadow-md hover:shadow-lg cursor-pointer"
            >
              <span>Contact Us Today</span>
              <i className="fa-regular fa-paper-plane text-sm" />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
